/**
 * The starting-balance block on the import's Confirm (TMMAPP-0162 item 6; TMMAPI-0191 item 3).
 * Shown under the counts while earlier history is included and the preview serves an
 * `openingCheck`: the opening moves back to `proposedOn` by exactly the earlier rows' sum, checked
 * against the file's own beginning balance. Money is in the account's currency (bank side, MM-D-63).
 *
 * - `MATCHES` and `NO_BANK_FIGURE`: one info sentence.
 * - `DIFFERS`: a warning with "Import anyway, I'll reconcile", unticked; the caller keeps Import
 *   disabled until it's ticked and then sends `acceptOpeningDifference: true`.
 * - A `409 OPENING_DIFFERS` on commit (`stale`, the preview went stale) says the served difference
 *   in the same block, unticked. Nothing retries by itself. The preview's "moves to {date}: {amount}"
 *   is no longer the server's figure, so the stale block says only the stale sentence and the served
 *   difference (TMMAPP-0164 item 6).
 *
 * The SPA computes nothing here: every figure is the server's.
 */
import { Alert, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import type { ImportOpeningCheck } from '@/api/imports';
import { MoneyText } from '@/components/MoneyText';
import { formatCivilDateNear } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { needsDifferenceAccepted, type StaleOpeningDifference } from './openingCheck';

export function EarlierHistoryOpening({
  check,
  stale,
  currency,
  today,
  accepted,
  onAcceptedChange,
  disabled,
}: {
  check: ImportOpeningCheck | null;
  stale: StaleOpeningDifference | null;
  currency: string | null;
  /** The viewer's civil today: the current year drops from the date (visual contract v2 §13). */
  today: string;
  accepted: boolean;
  onAcceptedChange: (accepted: boolean) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  if (check === null && stale === null) return null;
  const money = (minor: number | null) => (
    <MoneyText minor={minor} currency={currency} variant="body2" />
  );
  const values =
    check === null ? {} : { date: formatCivilDateNear(check.proposedOn, locale, today) };
  const movesTo =
    check === null ? null : (
      <Trans
        i18nKey={check.status === 'MATCHES' ? 'imports.opening.matches' : 'imports.opening.movesTo'}
        values={values}
        components={{ amount: money(check.proposedMinor) }}
      />
    );
  const differs = needsDifferenceAccepted(check, stale);
  return (
    <Alert
      severity={differs ? 'warning' : 'info'}
      data-opening-check={stale !== null ? 'STALE' : check?.status}
    >
      <Stack spacing={0.5}>
        {stale !== null ? (
          <Typography variant="body2">
            <Trans
              i18nKey="imports.opening.differsStale"
              components={{ difference: money(stale.differenceMinor) }}
            />
          </Typography>
        ) : check !== null && check.status === 'DIFFERS' ? (
          <Typography variant="body2">
            <Trans
              i18nKey="imports.opening.differs"
              values={values}
              components={{
                amount: money(check.proposedMinor),
                bank: money(check.bankStatedMinor),
                difference: money(check.differenceMinor),
              }}
            />
          </Typography>
        ) : (
          <Typography variant="body2">{movesTo}</Typography>
        )}
        {differs && (
          <FormControlLabel
            control={
              <Checkbox
                checked={accepted}
                disabled={disabled}
                onChange={(_event, checked) => onAcceptedChange(checked)}
              />
            }
            label={t('imports.opening.acceptDifference')}
          />
        )}
      </Stack>
    </Alert>
  );
}
